import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Vault, Eye, Flame, Wrench } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';

export default function Solutions() {
  const solutions = [
    {
      id: 'vault',
      icon: Vault,
      title: 'Vault Systems',
      description: 'Bank-grade security with multi-layer protection',
      image: 'https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&q=85',
      features: [
        'UL-rated vault doors up to TL-30x6',
        'Time-delay and dual-control mechanisms',
        'Advanced relocking systems',
        'Custom sizing and configurations',
      ],
      specs: [
        { label: 'Construction', value: 'Composite steel and concrete' },
        { label: 'Fire Rating', value: 'Up to 4 hours' },
        { label: 'Lock Type', value: 'Electronic or mechanical' },
        { label: 'Warranty', value: '10 years comprehensive' },
      ],
    },
    {
      id: 'biometric',
      icon: Lock,
      title: 'Biometric Access',
      description: 'Multi-factor authentication for critical areas',
      image: 'https://images.unsplash.com/photo-1668609537021-f377356358f9?w=800&q=85',
      features: [
        'Fingerprint, facial, and iris recognition',
        'Integration with existing access systems',
        'Audit trail and activity logging',
        'Anti-spoofing technology',
      ],
      specs: [
        { label: 'Recognition Speed', value: '<1 second' },
        { label: 'Accuracy', value: '99.9%+' },
        { label: 'User Capacity', value: 'Up to 10,000 users' },
        { label: 'Network', value: 'TCP/IP, WiFi, BLE' },
      ],
    },
    {
      id: 'doors',
      icon: Shield,
      title: 'Reinforced Doors',
      description: 'Industrial-grade protection against forced entry',
      image: 'https://images.unsplash.com/photo-1705776919715-fa052f797923?w=800&q=85',
      features: [
        'Ballistic and blast-resistant options',
        'Forced-entry rated (RC3-RC6)',
        'Weatherproof and corrosion-resistant',
        'Custom finishes and aesthetics',
      ],
      specs: [
        { label: 'Material', value: 'Steel, aluminum, composite' },
        { label: 'Thickness', value: '50mm - 120mm' },
        { label: 'Ballistic Rating', value: 'Up to NIJ Level IV' },
        { label: 'Certification', value: 'CE, UL, ISO' },
      ],
    },
    {
      id: 'access',
      icon: Eye,
      title: 'Access Control',
      description: 'Centralized management for multi-zone facilities',
      image: 'https://images.unsplash.com/photo-1697382608786-bcf4c113b86e?w=800&q=85',
      features: [
        'Card, mobile, and PIN-based access',
        'Real-time monitoring and alerts',
        'Integration with CCTV and alarms',
        'Cloud or on-premise management',
      ],
      specs: [
        { label: 'Doors Supported', value: 'Unlimited' },
        { label: 'Mobile App', value: 'iOS & Android' },
        { label: 'Reporting', value: 'Custom analytics' },
        { label: 'Integration', value: 'REST API available' },
      ],
    },
    {
      id: 'fire',
      icon: Flame,
      title: 'Fire-Resistant',
      description: 'Fire-rated security doors for critical protection',
      image: 'https://images.pexels.com/photos/965878/pexels-photo-965878.jpeg?w=800&q=85',
      features: [
        'Fire ratings up to 240 minutes',
        'Smoke and temperature seals',
        'Panic hardware compatible',
        'Security + fire code compliance',
      ],
      specs: [
        { label: 'Fire Rating', value: 'FD30 to FD240' },
        { label: 'Standards', value: 'BS, EN, NFPA' },
        { label: 'Operation', value: 'Automatic or manual' },
        { label: 'Finish', value: 'Paint or veneer' },
      ],
    },
    {
      id: 'maintenance',
      icon: Wrench,
      title: 'Maintenance Services',
      description: '24/7 support across UK and USA',
      image: 'https://images.pexels.com/photos/64609/pexels-photo-64609.jpeg?w=800&q=85',
      features: [
        'Scheduled inspections and servicing',
        'Emergency repair services',
        'Parts replacement and upgrades',
        'Compliance documentation',
      ],
      specs: [
        { label: 'Response Time', value: '<4 hours emergency' },
        { label: 'Coverage', value: 'UK & USA' },
        { label: 'Service Plans', value: 'Annual or multi-year' },
        { label: 'Certification', value: 'Manufacturer-certified techs' },
      ],
    },
  ];

  return (
    <div className="flex flex-col">
      {/* Header */}
      <section className="py-16 bg-card border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="outline" className="text-accent border-accent mb-4">
              Security Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-semibold mb-6">Comprehensive Protection Systems</h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              From vault doors to biometric access, our solutions deliver uncompromised security 
              for the world's most demanding facilities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Tabs */}
      <section className="py-16 bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="vault" className="w-full" data-testid="solutions-tabs">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 h-auto gap-2">
              {solutions.map((solution) => (
                <TabsTrigger
                  key={solution.id}
                  value={solution.id}
                  className="flex flex-col items-center justify-center py-3 px-2 h-auto"
                >
                  <solution.icon className="h-5 w-5 mb-1" />
                  <span className="text-xs">{solution.title}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {solutions.map((solution) => (
              <TabsContent key={solution.id} value={solution.id} className="mt-8">
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Image */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative aspect-square rounded-lg overflow-hidden metallic-surface"
                  >
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover opacity-70 mix-blend-luminosity"
                    />
                    <div className="noise-overlay rounded-lg" />
                  </motion.div>

                  {/* Content */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Card>
                      <CardHeader>
                        <div className="flex items-center space-x-3 mb-2">
                          <solution.icon className="h-8 w-8 text-accent" />
                          <CardTitle className="text-2xl">{solution.title}</CardTitle>
                        </div>
                        <CardDescription className="text-base">{solution.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Features */}
                        <div>
                          <h4 className="text-sm font-semibold mb-3">Key Features</h4>
                          <ul className="space-y-2">
                            {solution.features.map((feature, index) => (
                              <li key={index} className="text-sm text-muted-foreground flex items-start">
                                <span className="text-accent mr-2">•</span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Specs */}
                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="specs">
                            <AccordionTrigger>Technical Specifications</AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-3">
                                {solution.specs.map((spec, index) => (
                                  <div key={index} className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{spec.label}</span>
                                    <span className="font-medium">{spec.value}</span>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </div>
  );
}
